import React, { useState, useEffect } from 'react';
import { IconClock, IconMessageCircle, IconTrash, IconX } from '@tabler/icons-react';
import './ChatbotModal.css';

const decodeUserIdFromToken = (token) => {
  try {
    const payloadPart = token.split('.')[1];
    if (!payloadPart) return null;

    // JWTs are base64url encoded - normalize to standard base64 before decoding
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const decoded = atob(padded);
    const payload = JSON.parse(decoded);
    return payload.sub || payload.user_id || payload.uuid || null;
  } catch (err) {
    console.error('Failed to decode user id from token', err);
    return null;
  }
};

const resolveUserId = () => {
  // Preferred: decode JWT for authoritative id
  const token = localStorage.getItem('user');
  if (token) {
    const decoded = decodeUserIdFromToken(token);
    if (decoded) return decoded;
  }

  // Fallback: stored uuid set at login
  const storedUuid = localStorage.getItem('userid');
  if (storedUuid) return storedUuid;

  // Last resort: patient UUID from URL (keeps best-effort behavior)
  const queryParameters = new URLSearchParams(window.location.search);
  const patientUuid = queryParameters.get("user");
  return patientUuid;
};

export const ChatHistorySidebar = ({ onSelectSession, currentSessionId, onClose, onSessionDeleted }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deletingSessionId, setDeletingSessionId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('user');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Get the patient UUID from URL parameter - same as chat requests
      const queryParameters = new URLSearchParams(window.location.search);
      const patientUuid = queryParameters.get("user");

      console.log('Loading sessions for patient UUID:', patientUuid);

      const requestBody = { 
        action: 'get_sessions',
        user_uuid: patientUuid, // Include patient UUID from URL parameter
        target_user_id: patientUuid // Also try target_user_id in case backend expects this
      };

      console.log('Session request body:', JSON.stringify(requestBody));

      const response = await fetch('https://wellness-api.clovahealth.com/api/ai-health-insights/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to load sessions');
      }

      const data = await response.json();
      setSessions(data.data || []);
    } catch (err) {
      console.error('Error loading sessions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const processDeleteChunk = (data, sessionId) => {
    if (data.type === 'delete') {
      console.log(`Deleted ${data.deletedCount} items for conversation ${data.conversationId}`);
      return;
    }

    if (data.type === 'sessions') {
      setSessions(data.data || []);
      if (onSessionDeleted) {
        onSessionDeleted(sessionId);
      }
      return;
    }

    if (data.type === 'error') {
      throw new Error(data.error || 'Delete failed');
    }
  };

  const deleteSession = async (sessionId) => {
    setDeletingSessionId(sessionId);
    setError(null);

    try {
      const token = localStorage.getItem('user');
      if (!token) {
        throw new Error('No authentication token found');
      }

      const userId = resolveUserId();
      if (!userId) {
        throw new Error('Unable to determine user id from token or stored session');
      }

      // Include both authenticated user id and patient context to satisfy backend expectations
      const queryParameters = new URLSearchParams(window.location.search);
      const patientUuid = queryParameters.get("user");

      const deleteRequestBody = {
        action: 'delete_session',
        user_id: userId,          // from JWT / login
        user_uuid: patientUuid,   // patient context (for symmetry with other calls)
        target_user_id: patientUuid,
        sessionId: sessionId,
      };

      console.log('Delete session request body:', JSON.stringify(deleteRequestBody));

      const response = await fetch('https://wellness-api.clovahealth.com/api/ai-health-insights/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(deleteRequestBody),
      });

      if (!response.ok) {
        const errorText = await response.text();
        const parsedError = (() => {
          try {
            const parsed = JSON.parse(errorText);
            return parsed.error || parsed.message || errorText;
          } catch {
            return errorText;
          }
        })();
        throw new Error(parsedError || 'Failed to delete session');
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response body received');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (value) {
          buffer += decoder.decode(value, { stream: true });
        }

        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;
          try {
            const parsed = JSON.parse(trimmed);
            processDeleteChunk(parsed, sessionId);
          } catch (err) {
            console.error('Failed to parse delete stream chunk', trimmed, err);
          }
        }

        if (done) break;
      }

      if (buffer.trim()) {
        try {
          const parsed = JSON.parse(buffer.trim());
          processDeleteChunk(parsed, sessionId);
        } catch (err) {
          console.error('Failed to parse final delete stream chunk', buffer, err);
        }
      }
    } catch (err) {
      console.error('Error deleting session:', err);
      setError(err.message);
    } finally {
      setDeletingSessionId(null);
      setConfirmDeleteId(null);
    }
  };

  const handleDeleteClick = (event, sessionId) => {
    event.stopPropagation();
    if (confirmDeleteId === sessionId) {
      deleteSession(sessionId);
    } else {
      setConfirmDeleteId(sessionId);
      // Auto-reset confirmation after a short window
      setTimeout(() => {
        setConfirmDeleteId((current) => (current === sessionId ? null : current));
      }, 3000);
    }
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMins = Math.floor(diffMs / (1000 * 60));
        return diffMins <= 1 ? 'Just now' : `${diffMins} mins ago`;
      }
      return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
    }
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const handleSessionClick = (session) => {
    onSelectSession(session.conversationId);
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className="chat-history-sidebar">
      <div className="chat-history-header">
        <div className="chat-history-title">
          <IconClock size={20} />
          <span>Chat History</span>
        </div>
        {onClose && (
          <button className="chat-history-close-btn" onClick={onClose} aria-label="Close history">
            <IconX size={18} />
          </button>
        )}
      </div>

      <div className="chat-history-sessions">
        {loading && (
          <div className="chat-history-loading">
            <div className="chat-history-spinner" />
            <span>Loading conversations...</span>
          </div>
        )}

        {error && (
          <div className="chat-history-error">
            <p>{error || 'Something went wrong with chat history'}</p>
            <button onClick={loadSessions} className="chat-history-retry-btn">
              Retry
            </button>
          </div>
        )}

        {!loading && !error && sessions.length === 0 && (
          <div className="chat-history-empty">
            <IconMessageCircle size={48} opacity={0.3} />
            <p>No previous conversations</p>
            <span>Start a new chat to see history here</span>
          </div>
        )}

        {!loading && !error && sessions.length > 0 && (
          <>
            {sessions.map((session) => (
              <div
                key={session.conversationId}
                className={`chat-history-session ${
                  currentSessionId === session.conversationId ? 'active' : ''
                }`}
                onClick={() => handleSessionClick(session)}
              >
                <div className="chat-history-session-main">
                  <div className="chat-history-session-icon">
                    <IconMessageCircle size={18} />
                  </div>
                  <div className="chat-history-session-content">
                    <div className="chat-history-session-title">
                      {session.title || 'Conversation'}
                    </div>
                    <div className="chat-history-session-date">
                      {formatDate(session.created_at)}
                    </div>
                  </div>
                </div>
                <div className="chat-history-session-actions">
                  <button
                    type="button"
                    className={`chat-history-delete-btn ${
                      confirmDeleteId === session.conversationId ? 'confirm' : ''
                    }`}
                    onClick={(event) => handleDeleteClick(event, session.conversationId)}
                    disabled={deletingSessionId === session.conversationId}
                    aria-label="Delete conversation"
                  >
                    {deletingSessionId === session.conversationId
                      ? '…'
                      : confirmDeleteId === session.conversationId
                        ? 'Confirm'
                        : ''}
                    <IconTrash size={14} />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};
